import {
  getCookie,
  setCookie,
  delCookie,
  setLS,
  getLS,
  setSS,
  getSS,
  delLS,
} from 'hobbit-base/modules/utils';
/**
 * 用户身份认证模块(请使用this.$user)
 * 1、检测用户登录态
 * 2、跳转登录页
 * 3、退出登录
 */
export default class User {
  constructor($config, $fetch, $ctx) {
    this.$config = $config;
    this.$fetch = $fetch;
    this.$ctx = $ctx;
    this.tokenCheckTime = new Date(0);

    // #region 初始化孩子ID逻辑：App桥获取优先级 > URL > LS
    // URL参数学生ID初始化
    const studentid = this.$ctx.query.studentid;
    if (studentid) {
      this.vkStudentId(studentid);
    }

    /**
     * 获取App当前孩子id 以及 家长id
     * App环境内先走 v3版本 再走v2版本兜底
     */

    if (this.$ctx.isApp()) {
      window.$hobbit.$bridge.call({
        name: 'user/getRequestInfo',
        version: 3,
        timeout: 1e4,
      }).then(resp => {
        if (!resp.errorCode && resp.data?.data?.studentId) {
          this.vkStudentId(resp.data?.data?.studentId);
        }

        if (!resp.errorCode && resp.data?.data?.parentId) {
          this.vkUserId(resp.data?.data?.parentId);
        }

        // 走老版协议
        // if (!resp.data?.data?.studentId) {
        //   // 此桥已弃用
        //   window.$hobbit.$bridge.call({
        //     name: 'getCurrentStudent',
        //     version: 2,
        //     timeout: 1e4,
        //   }).then(res => {
        //     if (!res.errorCode && res.data?.data?.studentId) {
        //       this.vkStudentId(res.data?.data?.studentId);
        //     }
        //   });
        // }
      });
    }

    // URL参数注册源初始化
    const signupSource = this.$ctx.query.signupsource;
    if (signupSource) {
      this.signupSource(signupSource);
    }
  }

  /**
   * 获取或设置鉴权Token（注意，老网站cookie都是写在完整域名下，不跨主域的。需要兼容老网站。）
   */
  vkToken(v, expires) {
    if (v === null) {
      delCookie(this.$config.user.tokenKey, '/');
      return null;
    }
    if (v) {
      setCookie(this.$config.user.tokenKey, v, expires, '/');
      this.tokenCheckTime = new Date().getTime();
      return v;
    }
    return getCookie(this.$config.user.tokenKey);
  }

  /**
   * 持久化地区Code
   */
  regionCode(v) {
    if (v === null) {
      delLS(this.$config.user.regionCodeKey);
    }
    if (v) {
      setLS(this.$config.user.regionCodeKey, v);
      return v;
    }
    return getLS(this.$config.user.regionCodeKey);
  }

  /**
   * 持久化用户ID
   */
  vkUserId(v) {
    if (v) {
      setLS(this.$config.user.userIdKey, Number(v));
      return v;
    }

    const id = getLS(this.$config.user.userIdKey);
    if (id) return Number(id);
    return null;
  }

  /**
   * 持久化学生ID
   */
  vkStudentId(v) {
    if (v) {
      setLS(this.$config.user.studentIdKey, Number(v));
      return v;
    }

    const id = getLS(this.$config.user.studentIdKey);
    if (id) return Number(id);
    return null;
  }

  /**
   * 获取当前学生Level
   */
  async vkStudentLevel(courseId, preventDefaultAuthCheck = true) {
    const resp = await this.$fetch.get({
      url: '/rest/gw/api/class/student/info',
      params: { courseId },
      timeout: 1e4,
      preventDefaultAuthCheck,
      cache: { online: null }, // 内存级缓存
    });

    if (resp.errorCode) {
      return null;
    }
    const studentList = resp.data?.data ?? [];
    let currentStudentInfo = null;
    if (studentList && studentList.length) {
      const currentStudentId = this.vkStudentId();
      if (currentStudentId) {
        currentStudentInfo = studentList.find(s => String(s.studentId) === String(currentStudentId));
      }
    }

    return currentStudentInfo?.level
      ? {
        currentLevel: `LEVEL_${currentStudentInfo.level.split(' ')[1]}`, // 当前level
        nextBookLevel: currentStudentInfo.nextBookLevel, // 下级level ，约课相关需要使用此level，展示需要使用level
      }
      : {
        currentLevel: '',
        nextBookLevel: '',
      };
  }

  /**
   * 获取所有孩子信息
   */
  async getStudentInfo(fullInfo = false, preventDefaultAuthCheck = true, cache = { online: null }, checkEmptyStudents = true, backurl = window.location.href) {
    const resp = await this.$fetch.get({
      url: '/rest/gw/api/class/student/info',
      timeout: 1e4,
      preventDefaultAuthCheck,
      cache,
    });
    if (resp.errorCode) {
      return [];
    }
    const studentList = resp.data?.data ?? [];
    // 统一拦截没孩子进入补填孩子信息页面逻辑
    if (checkEmptyStudents && studentList.length === 0) {
      window.$hobbit.$app.pageTo(`${window.$hobbit.$theme.addchildURL}?from=supplement&backurl=${encodeURIComponent(backurl)}`);
      return [];
    }
    // 如果需要完整信息，则继续追加家庭接口获取的学生字段
    if (fullInfo) {
      const family = await this.getFamilyDetail();
      if (family) {
        const today = new Date();
        for (const student of studentList) {
          Object.assign(student, family.studentList.find(s => String(s.id) === String(student.studentId)));
          // 补充年龄属性
          if (student.birthday) {
            const birthday = Date.from(student.birthday);
            student.age = parseInt(((today.getFullYear() * 12 + today.getMonth())
              - (birthday.getFullYear() * 12 + birthday.getMonth())) / 12, 10);
          }
        }
      }
    }
    return studentList;
  }

  /**
   * 获取当前已选中的孩子信息
   */
  async getCurrentStudentInfo(fullInfo = false, preventDefaultAuthCheck = true, backurl) {
    const studentInfo = await this.getStudentInfo(fullInfo, preventDefaultAuthCheck, undefined, undefined, backurl);
    if (studentInfo && studentInfo.length) {
      const currentStudentId = this.vkStudentId();
      if (currentStudentId) {
        const finded = studentInfo.find(s => String(s.studentId) === String(currentStudentId));
        if (finded) return finded;
      }
      this.vkStudentId(studentInfo[0].studentId);
      return studentInfo[0];
    }
    return null;
  }

  /**
   * 获取家庭信息
   */
  async getFamilyDetail(autoRedirect = false, ignoreCache) {
    if (await this.isLogin(autoRedirect)) {
      const resp = await this.$fetch.get({
        url: '/rest/gw/api/intern/family/getDetail',
        timeout: 1e4,
        preventDefaultAuthCheck: true,
        cache: ignoreCache ? undefined : {
          online: 600 * 1e3,
          offline: 3600 * 1e3,
        },
      });
      if (!resp.errorCode && resp.data?.data?.parent?.id) {
        this.vkUserId(resp.data.data.parent.id);
        const studentList = resp.data?.data?.studentList;
        const currentStudentId = this.vkStudentId();
        if (!studentList?.length) {
          this.vkStudentId(null);
        } else if (!studentList.some((item) => item.id === currentStudentId)) {
          this.vkStudentId(studentList[0].id);
        }
        return resp.data.data;
      }
    }
    return null;
  }

  /**
   * 清除学生信息缓存
   */
  updateStudentInfoCache() {
    return this.$fetch.removeCache('/rest/gw/api/class/student/info');
  }

  /**
   * 清除家庭信息缓存
   */
  updateFamilyDetailCache() {
    return this.$fetch.removeCache('/rest/gw/api/intern/family/getDetail');
  }

  /**
   * 获取用户Tag数据
   */
  async getTag(tagId, ignoreCache) {
    const familyDetail = await this.getFamilyDetail(false, ignoreCache);
    if (!familyDetail) return null;
    const tags = familyDetail?.parent?.parentTags;
    if (!tags || !tags.length) return null;
    return tags.find(i => i.id === tagId);
  }

  /**
   * 是否已登录
   */
  async isLogin(autoRedirect = false, backurl) {
    const token = this.vkToken();
    if (token) {
      const nowTime = new Date().getTime();
      if (nowTime - this.tokenCheckTime < this.$config.user.tokenRecheckSeconds * 1e3) {
        // token免检期内
        return true;
      }

      // 距离上次token检测时间已经超过阀值，需要重新检查
      const passed = await this.checkToken(token);
      if (passed) {
        return true;
      }

      // 删除无效的token和登录id
      this.vkToken(null);
    }

    // 无token，判断是否要自动跳转登录页
    if (autoRedirect) {
      this.gotoLogin(backurl);
    }

    return false;
  }

  /**
   * 跳转登录页
   */
  // eslint-disable-next-line class-methods-use-this
  gotoLogin(backurl, ani) {
    /**
     * 跳转登录
     * 旧UA 家长端走 v1
     * 旧UA Tigerschool Korea 走v3
     * 新UA 全走v3
     */

    if (this.$ctx.match({ appProtocol: '2022', appName: ['VIPKID PARENT', 'VIPKID STUDY'] })) { // 旧UA 家长端 学生端
      return window.$hobbit.$bridge.call({
        name: 'loginRequired',
        params: {
          showClose: 1,
        },
        timeout: 1e4,
      });
    }

    if (this.$ctx.match({ appProtocol: '2023' })) { // 新UA
      return window.$hobbit.$bridge.call({
        name: 'user/loginRequired',
        version: 3,
        params: {
          showClose: 0,
        },
        timeout: 1e4,
      });
    }

    if (!backurl) {
      backurl = window.location.href;
    }

    const loginUrl = `${window.$hobbit.$theme.signinUrl}?backurl=${encodeURIComponent(backurl)}`;
    return window.$hobbit.$app.pageTo(loginUrl, ani);
  }

  /**
   * 检查token是否有效
   */
  async checkToken(tk) {
    if (!tk) {
      return false;
    }

    const resp = await this.$fetch.get({
      url: '/rest/gw/api/intern/user/checkLoginStatus',
      timeout: 3000,
    });

    if (!resp.errorCode && resp.data) {
      this.tokenCheckTime = new Date().getTime();
      return true;
    }
    return false;
  }

  /**
   * 退出登录态
   */
  logout(backTo = '/') {
    // 请求服务器端删除token
    // this.$fetch.post({
    //   url: '/rest/gw/api/intern/user/logout',
    //   timeout: 3000,
    // });
    // 删除本地的登录态信息
    this.vkToken(null);
    if (backTo !== null) {
      window.$hobbit.$app.pageTo(backTo).then(code => {
        if (code === 16) {
          window.$hobbit.$app.pageReload();
        }
      });
    }
  }

  /**
   * 获取用户邀请码
   */
  async getReferralCode(autoRedirect = false) {
    if (await this.isLogin(autoRedirect)) {
      const resp = await this.$fetch.get({
        url: '/rest/gw/api/intern/parent/getReferral',
        timeout: 1e4,
        preventDefaultAuthCheck: true,
        cache: {
          online: 24 * 3600 * 1e3,
          offline: 240 * 3600 * 1e3,
        },
      });
      if (resp.errorCode) {
        return null;
      }
      return resp.data?.data || null;
    }
    return null;
  }

  /**
   * 设置、获取用户注册来源点位，用于注册接口上报的注册渠道计算，最终后端计算归因
   */
  signupSource(source) {
    if (source) {
      setSS(this.$config.user.signupSourceKey, source);
      return source;
    }

    return getSS(this.$config.user.signupSourceKey);
  }
}
